import { useState, useEffect } from 'react';
import {
  Text,
  Group,
  Stack,
  Box,
  Loader,
  Progress,
  Badge,
  Paper,
} from '@mantine/core';
import type { ParticipantCohortAttendance } from '../../../types';
import { participantsApi } from '../../../services/api/participants';
import { ENGAGEMENT_RATING_MAP } from '../../../config/engagementRatingConfig';
import { AttendanceRate } from '../../../components/common';

interface CohortAttendanceSectionProps {
  cohortId: string;
  participantId: string;
  isActive: boolean;
}

export function CohortAttendanceSection({ cohortId, participantId, isActive }: CohortAttendanceSectionProps) {
  const [data, setData] = useState<ParticipantCohortAttendance | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isActive || !participantId || data) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await participantsApi.getCohortAttendance(cohortId, participantId);
        setData(result);
      } catch {
        // Silently fail - section just won't show data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isActive, cohortId, participantId, data]);

  if (loading) {
    return (
      <Box mb="xl">
        <Text
          size="xs"
          fw={600}
          c="dimmed"
          mb="md"
          style={{ letterSpacing: '0.5px', textTransform: 'uppercase' }}
        >
          Cohort Attendance
        </Text>
        <Box ta="center" py="xl">
          <Loader size="sm" />
        </Box>
      </Box>
    );
  }

  if (!data || data.total_sessions === 0) {
    return (
      <Box mb="xl">
        <Text
          size="xs"
          fw={600}
          c="dimmed"
          mb="md"
          style={{ letterSpacing: '0.5px', textTransform: 'uppercase' }}
        >
          Cohort Attendance
        </Text>
        <Text size="sm" c="dimmed">
          No attendance data recorded
        </Text>
      </Box>
    );
  }

  const { sessions_attended, sessions_partial, sessions_absent, total_sessions, attendance_percentage, attendance_marked, attendance_population, engagement_ratings, average_engagement_rating } = data;
  const avgConfig = average_engagement_rating ? ENGAGEMENT_RATING_MAP.get(Math.round(average_engagement_rating)) : null;

  return (
    <Box mb="xl">
      <Text
        size="xs"
        fw={600}
        c="dimmed"
        mb="md"
        style={{ letterSpacing: '0.5px', textTransform: 'uppercase' }}
      >
        Cohort Attendance
      </Text>

      <Stack gap="md">
        {/* Attendance Summary */}
        <Paper withBorder p="md" radius="sm">
          <Group gap="sm" mb="xs">
            <Text size="sm" fw={600}>Attendance</Text>
          </Group>

          {/* MED-1266: total_sessions is now the recorded-outcome count; the rate + its
              marking coverage are shown via the shared AttendanceRate component (AC1/AC10). */}
          <Group justify="space-between" mb={6}>
            <Text size="sm">
              {sessions_attended + sessions_partial} of {total_sessions} attended
            </Text>
            <AttendanceRate
              rate={attendance_percentage}
              marked={attendance_marked}
              population={attendance_population}
              variant="inline"
              size="sm"
              fw={600}
            />
          </Group>

          <Progress.Root size="sm" radius="sm">
            {sessions_attended > 0 && (
              <Progress.Section value={(sessions_attended / total_sessions) * 100} color="teal" />
            )}
            {sessions_partial > 0 && (
              <Progress.Section value={(sessions_partial / total_sessions) * 100} color="yellow" />
            )}
            {sessions_absent > 0 && (
              <Progress.Section value={(sessions_absent / total_sessions) * 100} color="red" />
            )}
          </Progress.Root>

          <Group gap="lg" mt="xs">
            <Group gap={4}>
              <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-teal-6)' }} />
              <Text size="xs" c="dimmed">Attended ({sessions_attended})</Text>
            </Group>
            <Group gap={4}>
              <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-yellow-6)' }} />
              <Text size="xs" c="dimmed">Partial ({sessions_partial})</Text>
            </Group>
            <Group gap={4}>
              <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-red-6)' }} />
              <Text size="xs" c="dimmed">Absent ({sessions_absent})</Text>
            </Group>
          </Group>
        </Paper>

        {/* Engagement Ratings */}
        {(engagement_ratings.length > 0 || average_engagement_rating !== null) && (
          <Paper withBorder p="md" radius="sm">
            <Group gap="sm" mb="xs" justify="space-between">
              <Text size="sm" fw={600}>Engagement</Text>
              {average_engagement_rating !== null && avgConfig && (
                <Badge size="sm" color={avgConfig.color} variant="light">
                  Avg: {average_engagement_rating} - {avgConfig.label}
                </Badge>
              )}
            </Group>

            {engagement_ratings.length > 0 ? (
              <Stack gap={6}>
                {engagement_ratings.map((er, idx) => {
                  const ratingConfig = ENGAGEMENT_RATING_MAP.get(Number(er.rating));
                  return (
                    <Group key={idx} gap="sm" py={2}>
                      <Text size="xs" c="dimmed" w={70} style={{ flexShrink: 0 }}>
                        {er.session_number != null ? `Session ${er.session_number}` : er.session_title}
                      </Text>
                      <Badge size="xs" color={ratingConfig?.color || 'gray'} variant="filled" w={20} h={20} p={0} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {er.rating}
                      </Badge>
                      <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                        {ratingConfig?.label || `Rating ${er.rating}`}
                      </Text>
                    </Group>
                  );
                })}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">No engagement ratings recorded</Text>
            )}
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
